// Stripe Dashboard CSV. Mirrors load_stripe_csv.
//
// Two header conventions:
//   - Classic Payments: id, created, amount, currency, description
//   - Payments and Charges Export: id, Created (UTC), Amount, Currency, ...
// Both have id+created together → detect by header sniff. Case-insensitive
// column lookup handles either shape.
//
// Sign: positive amount = received (income). Flip to our convention.

import {
  type Transaction,
  newTransaction,
  makeId,
  parseAmount,
  parseDate,
  normalizeVendor,
  isInterPortalTransfer,
} from "../common.js";
import { parseCsv, toDictRows, findHeaderIndex } from "../csv.js";

function ciGet(row: Record<string, string>, ...keys: string[]): string {
  const norm: Record<string, string> = {};
  for (const k of Object.keys(row)) norm[k.toLowerCase().trim()] = row[k];
  for (const k of keys) {
    const v = norm[k.toLowerCase().trim()];
    if (v !== undefined && v !== "") return v;
  }
  return "";
}

export async function loadStripeCsv(
  text: string,
  filename: string,
): Promise<Transaction[]> {
  const rows = parseCsv(text);
  const headerIdx = findHeaderIndex(rows, ["id", "created"]);
  if (headerIdx < 0 || headerIdx >= rows.length - 1) return [];
  const dicts = toDictRows(rows[headerIdx], rows.slice(headerIdx + 1));

  const out: Transaction[] = [];
  for (const row of dicts) {
    const created = ciGet(row, "Created (UTC)", "created", "Created");
    // ISO timestamp: split at "T" or " " to get the date portion.
    const datePart = created.trim().split(/[T ]/, 1)[0];
    const date = datePart ? parseDate(datePart) : "";
    if (!date) continue;
    const amountRaw = parseAmount(ciGet(row, "amount", "Amount", "Net") || "0");
    const amount = -amountRaw; // Stripe positive = received → income (negative)
    const ccy = (ciGet(row, "currency", "Currency") || "USD").trim().toUpperCase();
    const desc = (
      ciGet(row, "customer_description", "Customer Description") ||
      ciGet(row, "card_name", "Card Name") ||
      ciGet(row, "description", "Description") ||
      "Stripe charge"
    ).trim();
    if (isInterPortalTransfer(desc)) continue;

    const accountLabel = `Stripe (${ccy})`;
    const txId = ciGet(row, "id", "Charge ID", "charge_id").trim();
    const id = txId || (await makeId("stripe", accountLabel, date, amount, desc));

    out.push(
      newTransaction({
        id,
        date,
        vendor_raw: desc,
        vendor_key: normalizeVendor(desc) || "stripe",
        amount: Math.round(amount * 100) / 100,
        portal: "stripe",
        account: accountLabel,
        account_kind: "debit",
        source_file: filename,
        raw: row,
      }),
    );
  }
  return out;
}
